// Pure rules for the Unmatched replies screen: who may resolve, which state shows, wording.
import { isNotConnected, isConflict, NOTE_MAX } from '@/lib/reply-events-client.js'

export const APOLLO_RESOLVES = 'Apollo resolves'

// The browser only hides. The Engine server decides who may resolve.
export const canResolve = role => role === 'admin'

export const STATUS_WORD = { unmatched: 'Unmatched', ambiguous: 'Ambiguous', pending: 'Pending' }
export const STATUS_TONE = { unmatched: 'error', ambiguous: 'warning', pending: 'neutral' }

export const ACTION_LABEL = { no_action_needed: 'No action needed', handled_manually: 'Handled manually' }

export const COPY = {
  intro: 'An unmatched reply is a reply Engine could not tie to an email it sent, or has not finished matching. Engine holds it and blocks sending from the mailbox it came to.',
  resolving: 'Resolving records what you did about the reply and its note, and releases the hold. A reply can be resolved once, and it cannot be undone.',
  empty: 'No held replies. No mailbox is blocked by one.',
  notConnected: 'Not connected yet.',
  notConnectedDetail: 'Held replies appear here once Engine can list them. Nothing is being hidden: this is not an empty list.',
  loading: 'Loading held replies',
  conflict: 'Someone else already resolved this reply, or it is no longer held. The list has been refreshed.',
}

// loading | rows | empty | notConnected | error. Empty only ever follows a successful answer with nothing held.
export function deriveState({ phase, data }) {
  if (phase === 'loading') return 'loading'
  if (phase === 'notConnected') return 'notConnected'
  if (phase === 'error') return 'error'
  if (!data) return 'error'
  return data.summary.held === 0 && data.events.length === 0 ? 'empty' : 'rows'
}

// What a failed list call means for the screen.
export function phaseForError(e) {
  return isNotConnected(e) ? 'notConnected' : 'error'
}

// One list call as a screen phase. A 404 or 503 is notConnected, any other failure is error.
// Success carries the data, so empty is reachable only through a real answer.
export async function loadHeld(client, signal) {
  try {
    return { phase: 'ready', data: await client.list({ signal }), error: null }
  } catch (e) {
    if (signal?.aborted || e?.name === 'AbortError') return { phase: 'aborted', data: null, error: null }
    return { phase: phaseForError(e), data: null, error: errorMessage(e) }
  }
}

export function errorMessage(e) {
  return e && typeof e.message === 'string' && e.message ? e.message : 'The held replies could not be loaded.'
}

export function noteProblem(note) {
  const trimmed = typeof note === 'string' ? note.trim() : ''
  if (!trimmed) return 'Write a note about what you did.'
  if (trimmed.length > NOTE_MAX) return `Keep the note to ${NOTE_MAX} characters.`
  return null
}

const pad = n => String(n).padStart(2, '0')

// MM/DD/YYYY h:mm AM, in the viewer's time zone (the screenshot harness pins UTC).
export function formatReceived(ms) {
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return 'Unknown time'
  const h = d.getHours() % 12 || 12
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${h}:${pad(d.getMinutes())} ${d.getHours() < 12 ? 'AM' : 'PM'}`
}

// What a resolve attempt came to. Never throws.
//   resolved | conflict | invalid | error | busy
export function createResolveController({ client, onResolved, onConflict }) {
  let busy = false
  return {
    get busy() { return busy },
    async submit(eventId, { action, note }) {
      if (busy) return { kind: 'busy' }
      if (noteProblem(note)) return { kind: 'invalid', message: noteProblem(note) }
      busy = true
      try {
        await client.resolve(eventId, { action, note })
        await onResolved?.(eventId)
        return { kind: 'resolved' }
      } catch (e) {
        if (isConflict(e)) {
          await onConflict?.(eventId)
          return { kind: 'conflict', message: COPY.conflict }
        }
        return { kind: 'error', message: errorMessage(e) }
      } finally {
        busy = false
      }
    },
  }
}
